import React from 'react';

import { LoadingButton } from '@atlaskit/button';
import DropdownMenu from '@atlaskit/dropdown-menu';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';

import { FocusInterceptor } from '@qality/common-components/src/atoms/FocusInterceptor';
import { ExecutionStatus } from '@qality/common-model';
import { CssUtils, StatusUtils, StatusesGroup } from '@qality/common-utils';

import { LoadingButtonWrapperStyled } from './ExecutionStatusSelect.styled';
import { ExecutionStatusSelectOptions } from './ExecutionStatusSelectOptions';

type ExecutionStatusSelectProps = {
  value: ExecutionStatus | null;
  statuses: Array<ExecutionStatus>;
  onChange: (value: ExecutionStatus) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
};

type ExecutionStatusSelectComponentProps = {
  value: ExecutionStatus | null;
  statuses: StatusesGroup;
  onChange: (value: ExecutionStatus) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
};

export function ExecutionStatusSelect({
  value,
  statuses,
  onChange,
  isLoading = false,
  isDisabled = false,
}: ExecutionStatusSelectProps): JSX.Element {
  if (!value) {
    if (isLoading) {
      return (
        <LoadingButton
          isLoading
          isDisabled
          style={{
            minWidth: '120px',
          }}
        />
      );
    }

    return null;
  }

  return (
    <ExecutionStatusSelectComponent
      value={value}
      statuses={StatusUtils.groupStatusesByCategoryWithoutMapping(statuses)}
      onChange={onChange}
      isLoading={isLoading}
      isDisabled={isDisabled}
    />
  );
}

function ExecutionStatusSelectComponent({
  value,
  statuses,
  onChange,
  isLoading,
  isDisabled,
}: ExecutionStatusSelectComponentProps) {
  const handleChange = (newStatus: ExecutionStatus): void => {
    onChange(newStatus);
  };

  const textColorVar = CssUtils.cssDsTokenVar(value.color.textToken);
  const backgroundColorVar = CssUtils.cssDsTokenVar(
    value.color.backgroundToken
  );

  return (
    <FocusInterceptor
      render={(interceptFocus) => {
        return (
          <DropdownMenu
            autoFocus
            onOpenChange={interceptFocus}
            isLoading={isLoading}
            trigger={({ triggerRef, ...props }) => (
              <LoadingButtonWrapperStyled
                customStyle={{
                  color: textColorVar,
                  backgroundColor: backgroundColorVar,
                }}
              >
                <LoadingButton
                  {...props}
                  ref={triggerRef}
                  isDisabled={isDisabled || isLoading}
                  isLoading={isLoading}
                  iconAfter={
                    <HipchatChevronDownIcon label='Dropdown`s trigger' />
                  }
                >
                  {value.name}
                </LoadingButton>
              </LoadingButtonWrapperStyled>
            )}
          >
            <ExecutionStatusSelectOptions
              statusesCategories={statuses}
              onClick={handleChange}
            />
          </DropdownMenu>
        );
      }}
    />
  );
}
